import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Col, Container, Row, Spinner } from "react-bootstrap"
import { Link, useParams } from "react-router-dom"
import Select from "react-select"
import AsyncSelect from "react-select/async"
import { toast } from "react-toastify"
import CustBreadcrumb from "components/CustBreadcrumb"
import Loader from "components/Loader"
import PlaceholdersModal from "./PlaceholdersModal"
import {
  getNotificationSetting,
  updateNotificationSetting
} from "requesters/notificationSettingsRequester"
import { getUsers } from "requesters/userRequester"
import { roleCheck } from "utils/index"
import { OPERATIONS } from "utils/constants"
import { getCurrentRole, getCurrentUser, getBaseUrl } from "apps/App/reducers"
import _ from "lodash"

const ROLE_OPTIONS = [
  { value: null, label: "All" },
  { value: "manager", label: "Manager" },
  { value: "user", label: "User" }
]

const SYSTEM_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" }
]

const toastOptions = {
  toastId: "notification-settings-page",
  containerId: "main"
}

function groupOptionValue(group) {
  if (group.kind === "system_role") return `system_role:${group.systemRole}`
  return `team:${group.teamId}:${group.teamRole || "all"}`
}

function parseGroupOption(option) {
  if (option.kind === "system_role") {
    return {
      kind: "system_role",
      systemRole: option.systemRole,
      teamId: null,
      teamRole: null
    }
  }
  return {
    kind: "team",
    teamId: option.teamId,
    teamRole: option.teamRole,
    systemRole: null
  }
}

function buildGroupOptions(teams) {
  const teamOpts = (teams || []).flatMap(team => (
    ROLE_OPTIONS.map(role => ({
      value: `team:${team.id}:${role.value || "all"}`,
      label: `${team.name} (${role.label})`,
      kind: "team",
      teamId: team.id,
      teamRole: role.value
    }))
  ))
  const systemOpts = SYSTEM_ROLE_OPTIONS.map(opt => ({
    value: `system_role:${opt.value}`,
    label: opt.label,
    kind: "system_role",
    systemRole: opt.value
  }))
  return [...teamOpts, ...systemOpts]
}

function userOption(user) {
  return {
    value: parseInt(user.id, 10),
    label: user.email ? `${user.name} (${user.email})` : user.name
  }
}

export default function NotificationSettingEdit() {
  const { id } = useParams()
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser.attributes.role

  const [setting, setSetting] = useState(null)
  const [teams, setTeams] = useState([])
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [fallbackUser, setFallbackUser] = useState(null)
  const [requesting, setRequesting] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [allPlaceholders, setAllPlaceholders] = useState([])
  const [showPlaceholdersModal, setShowPlaceholdersModal] = useState(false)

  if (!roleCheck(systemRole, currentRole, OPERATIONS) && !["admin", "super_admin"].includes(systemRole)) {
    window.location = "/"
  }

  const groupOptions = useMemo(() => buildGroupOptions(teams), [teams])

  const loadUserOptions = useCallback((input) => (
    getUsers(baseUrl, {
      by_name_or_email: input || undefined,
      by_status: "active",
      page: 1,
      per_page: 25
    }).then(res => {
      if (res.status !== 200) return []
      return _.values(_.get(res.response, "user", {})).map(user => userOption({
        id: user.id,
        name: user.attributes.name,
        email: user.attributes.email
      }))
    })
  ), [baseUrl])

  useEffect(() => {
    if (!baseUrl) return

    async function fetchData() {
      setRequesting(true)
      const { response, error } = await getNotificationSetting(baseUrl, id)

      if (response) {
        const row = _.get(response, `notificationSetting.${id}`)
          || _.values(response.notificationSetting || {})[0]
        const metaTeams = _.get(response, "meta.teams", [])
        const options = buildGroupOptions(metaTeams)
        const attrs = row.attributes

        setTeams(metaTeams)
        setSetting(row)
        setTitle(attrs.title || "")
        setBody(attrs.body || "")
        setAllPlaceholders(_.get(response, "meta.placeholders", []))
        setLoadError(false)
        setSelectedGroups((attrs.recipientGroups || []).map(group => {
          const value = groupOptionValue(group)
          return options.find(opt => opt.value === value) || {
            value,
            label: group.label,
            kind: group.kind,
            teamId: group.teamId,
            teamRole: group.teamRole,
            systemRole: group.systemRole
          }
        }))
        setSelectedUsers((attrs.recipientUsers || []).map(userOption))
        setFallbackUser(attrs.fallbackUser ? userOption(attrs.fallbackUser) : null)
      } else if (error) {
        console.error(error)
        setLoadError(true)
        toast.error("Failed to load notification setting", toastOptions)
      }
      setRequesting(false)
    }

    fetchData()
  }, [baseUrl, id])

  const handleSave = async () => {
    setSaving(true)
    const { response, error } = await updateNotificationSetting(baseUrl, id, {
      title,
      body,
      fallbackUserId: fallbackUser ? fallbackUser.value : null,
      recipientUserIds: selectedUsers.map(user => user.value),
      recipientGroups: selectedGroups.map(parseGroupOption)
    })
    if (response) {
      toast.success("Notification updated", toastOptions)
    } else {
      console.error(error)
      toast.error("Failed to update notification", toastOptions)
    }
    setSaving(false)
  }

  if (requesting) {
    return (
      <Container fluid className="pb-5">
        <Loader />
      </Container>
    )
  }

  if (loadError || !setting) {
    return (
      <Container fluid className="pb-5">
        <p className="text-muted">Unable to load this notification setting.</p>
        <Link className="btn btn-outlined-blue" to="/settings/notification-settings">
          Back to list
        </Link>
      </Container>
    )
  }

  const { attributes } = setting

  return (
    <Container fluid className="pb-5">
      <CustBreadcrumb
        links={[
          ["Settings", "/settings"],
          ["Notifications", "/settings/notification-settings"]
        ]}
        active={attributes.name} />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{attributes.name}</h2>
        <Link className="btn btn-outlined-blue" to="/settings/notification-settings">
          Back to list
        </Link>
      </div>
      {attributes.description && <p className="text-muted mb-4">{attributes.description}</p>}

      <Row>
        <Col md={8}>
          <div className="card">
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input
                  className="form-control"
                  value={title}
                  onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label">Body</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={body}
                  onChange={e => setBody(e.target.value)} />
                <small className="text-muted d-block mt-2">
                  {(attributes.placeholders || []).length > 0 && (
                    <>
                      Placeholders: {(attributes.placeholders || []).map(token => `%{${token}}`).join(", ")}
                      {" "}
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    onClick={() => setShowPlaceholdersModal(true)}>
                    more
                  </button>
                </small>
              </div>
              <div className="mb-3">
                <label className="form-label">Teams / roles</label>
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  options={groupOptions}
                  value={selectedGroups}
                  onChange={value => setSelectedGroups(value || [])}
                  placeholder="Select teams or roles" />
              </div>
              <div className="mb-3">
                <label className="form-label">Users</label>
                <AsyncSelect
                  isMulti
                  isClearable
                  cacheOptions
                  classNamePrefix="react-select"
                  defaultOptions={selectedUsers}
                  loadOptions={loadUserOptions}
                  value={selectedUsers}
                  onChange={value => setSelectedUsers(value || [])}
                  placeholder="Search users" />
              </div>
              <div className="mb-4">
                <label className="form-label">Fallback user</label>
                <AsyncSelect
                  isClearable
                  cacheOptions
                  classNamePrefix="react-select"
                  defaultOptions={fallbackUser ? [fallbackUser] : true}
                  loadOptions={loadUserOptions}
                  value={fallbackUser}
                  onChange={value => setFallbackUser(value)}
                  placeholder="Search fallback user" />
                <small className="text-muted">Sent if all selected recipients are on leave.</small>
              </div>
            </div>
          </div>
          <button
            className="btn btn-red"
            type="button"
            onClick={handleSave}
            disabled={saving}>
            {saving ? <Spinner animation="border" role="status" size="sm" /> : "Save"}
          </button>
        </Col>
      </Row>
      <PlaceholdersModal
        show={showPlaceholdersModal}
        onHide={() => setShowPlaceholdersModal(false)}
        placeholders={allPlaceholders} />
    </Container>
  )
}
